import { useState } from "react";
import { Plus, Pencil, Power } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common/PageHeader";
import { DataTable } from "../components/common/DataTable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const StatusBadge = (r) => <Badge className={r.is_active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-200 text-slate-600"}>{r.is_active ? "Active" : "Inactive"}</Badge>;

export default function DefectReasonMaster() {
  const { has } = useAuth();
  const canManage = has("DEFECT_REASON.MANAGE");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ reason_code: "", name: "" });
  const [refresh, setRefresh] = useState(0);
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setEditing(null); setForm({ reason_code: "", name: "" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ reason_code: r.reason_code, name: r.name }); setOpen(true); };

  const save = async () => {
    if (!form.reason_code.trim() || !form.name.trim()) { toast.error("Code and name are required"); return; }
    if (saving) return;
    setSaving(true);
    try {
      if (editing) await api.put(`/defect-reasons/${editing.id}`, { name: form.name });
      else await api.post("/defect-reasons", { reason_code: form.reason_code, name: form.name });
      toast.success("Defect reason saved"); setOpen(false); setRefresh((n) => n + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const toggle = async (r) => {
    try { await api.patch(`/defect-reasons/${r.id}/status?is_active=${!r.is_active}`); setRefresh((n) => n + 1); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const columns = [
    { key: "reason_code", label: "Code", mono: true },
    { key: "name", label: "Reason", render: (r) => <span className="font-medium text-slate-900">{r.name}</span> },
    { key: "status", label: "Status", render: StatusBadge },
  ];

  return (
    <div>
      <PageHeader title="Defect Reason Master" subtitle="Governed list of defect reasons used when marking materials defective" />
      {canManage && <div className="mb-3 flex justify-end"><Button data-testid="defect-reason-add" onClick={openCreate} size="sm" className="bg-blue-600 hover:bg-blue-700"><Plus className="mr-1.5 h-4 w-4" /> New Reason</Button></div>}
      <DataTable
        testid="defect-reason-table"
        columns={columns}
        searchPlaceholder="Search reasons..."
        refreshKey={refresh}
        load={(params) => api.get("/defect-reasons/manage", { params }).then((r) => r.data)}
        emptyText="No defect reasons yet"
        rowActions={canManage ? (r) => (
          <div className="flex justify-end gap-1">
            <Button data-testid={`defect-reason-edit-${r.id}`} variant="ghost" size="sm" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
            <Button data-testid={`defect-reason-toggle-${r.id}`} variant="ghost" size="sm" onClick={() => toggle(r)}><Power className={`h-4 w-4 ${r.is_active ? "text-red-500" : "text-emerald-500"}`} /></Button>
          </div>
        ) : null}
      />
      <Dialog open={open} onOpenChange={(o) => !saving && setOpen(o)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Defect Reason" : "New Defect Reason"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Reason Code *</Label><Input data-testid="defect-reason-code-input" disabled={!!editing} value={form.reason_code} onChange={(e) => setForm({ ...form, reason_code: e.target.value })} className="mt-1" /></div>
            <div><Label>Reason Name *</Label><Input data-testid="defect-reason-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" disabled={saving} onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="defect-reason-save" disabled={saving} onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
